import useFetchEntity from 'hooks/useFetchEntity';
import { formatDateToLongFormat, formatTimeStringToHHMM } from 'utils/IGdateTimeFunctions';

import "@fontsource/audiowide";
import "@fontsource/bebas-neue";
import "@fontsource/covered-by-your-grace";
import "@fontsource/economica";
import "@fontsource/righteous";
import "@fontsource/russo-one";

import DebugPrinter from 'components/Debug/DebugPrinter';
import SizeSelector from 'components/Infographics/Filters/SizeSelector';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import useSearchParamsMulti from 'hooks/useSearchParamsMulti';
import usePersistentStore from 'store/persistentStore';
import BackgroundSelector from './Filters/BackgroundSelector';
import IgImgSelector from './Filters/IgImgSelector';
import GameSelector from './Filters/GameSelector';
import MarginEditor from './Filters/MarginEditor';
import CompetitionLogo from './Parts/CompetitionLogo';
import IGImg from './Parts/IGImg';
import LogoOrTitle from './Parts/LogoOrTitle';
dayjs.extend(customParseFormat);

const IG4_YoutubeThumbnail = ({ showData, exportRef }) => {
  const { useSearchParamSingle } = useSearchParamsMulti();
  const [gameId, setGameId] = useSearchParamSingle('gameId', null);
  const size = usePersistentStore((state) => state.get('ig-youtubethumbnail-size'));
  const setSize = usePersistentStore((state) => state.set('ig-youtubethumbnail-size'));
  const SIZE_PRESETS = ['LANDSCAPE', 'CUSTOM'];
  const DEFAULT_SIZE = { width: 950, height: 534, preset: 'LANDSCAPE' };

  const igname = 'youtubethumbnail';
  const marginDefaults = { top: 0, left: 0, bottom: 0, right: 0 };
  const bgImgMargin = usePersistentStore((state) => state.get(`ig-${igname}-margin`)) ?? marginDefaults;
  const setBgImgMargin = usePersistentStore((state) => state.set(`ig-${igname}-margin`));

  // Background image and color
  const backgroundImgUrl = usePersistentStore((state) => state.get('ig-youtubethumbnail-background-img-url'));
  const setBackgroundImgUrl = usePersistentStore((state) => state.set('ig-youtubethumbnail-background-img-url'));
  const backgroundColor = usePersistentStore((state) => state.get('ig-youtubethumbnail-background-img-color'));
  const setBackgroundColor = usePersistentStore((state) => state.set('ig-youtubethumbnail-background-img-color'));
  const backgroundMode = usePersistentStore((state) => state.get('ig-youtubethumbnail-background-mode'))
    ?? (backgroundImgUrl ? 'IMAGE' : backgroundColor ? 'COLOR' : 'DEFAULT');
  const setBackgroundMode = usePersistentStore((state) => state.set('ig-youtubethumbnail-background-mode'));

  const { data: gameDetail } = useFetchEntity('Game', gameId, 'HomeTeam,AwayTeam,Venue,Phase>Group>Season>Competition');

  const metadata = {
    HomeTeamLogo: gameDetail?.HomeTeam?.logoUrl?.replace(
      "[size]",
      "cropped_md",
    ),
    HomeTeamName: gameDetail?.HomeTeam?.nick,
    AwayTeamLogo: gameDetail?.AwayTeam?.logoUrl?.replace(
      "[size]",
      "cropped_md",
    ),
    AwayTeamName: gameDetail?.AwayTeam?.nick,
    Date: gameDetail?.startDate,
    Time: gameDetail?.startTime,
    VenueName: gameDetail?.Venue?.name,
    VenueLogo: gameDetail?.Venue?.logoUrl?.replace("[size]", "cropped_md"),
    CompetitionName: gameDetail?.Phase?.Group?.Season?.Competition?.name,
    CompetitionLogo:
      gameDetail?.Phase?.Group?.Season?.Competition?.logoUrl?.replace(
        "[size]",
        "cropped_md",
      ),
    SeasonName: gameDetail?.Phase?.Group?.Season?.name,
    PhaseLogo: gameDetail?.Phase?.logoUrl?.replace("[size]", "cropped_md"),
  };

  return (

    <>
      <div className="grid grid-cols-[auto,1fr] gap-3 items-center">

        <span>Game:</span>
        <GameSelector
          persistentStoreKey="ig-youtubethumbnail-game"
          value={gameId}
          onChange={(gid) => { setGameId(gid) }}
        />

        <span>Background:</span>
        <BackgroundSelector
          backgroundMode={backgroundMode}
          setBackgroundMode={setBackgroundMode}
          backgroundImgUrl={backgroundImgUrl}
          setBackgroundImgUrl={setBackgroundImgUrl}
          backgroundColor={backgroundColor}
          setBackgroundColor={setBackgroundColor}
          igId={4}
        />

        <span>Dimensions:</span>
        <div className="flex gap-2 items-center">
          <SizeSelector
            value={size}
            onChange={setSize}
            presets={SIZE_PRESETS}
            defaultValue={DEFAULT_SIZE}
          />
        </div>

        <span>Margin:</span>
        <div className="flex gap-2 items-center">
          <MarginEditor
            margin={bgImgMargin}
            setMargin={setBgImgMargin}
            defaults={marginDefaults}
          />
        </div>

        <div />
      </div>

      {showData && (<DebugPrinter data={[gameDetail, metadata]}></DebugPrinter>)}

      {gameId &&
        <article
          ref={exportRef}
          className="mx-auto grid aspect-video w-[950px] grid-cols-3 font-frontline text-white bg-hockey-page-bg h-fit"
          style={{
            width: `${size?.width}px`,
            height: `${size?.height}px`,
            paddingTop: `${bgImgMargin.top}px`,
            paddingLeft: `${bgImgMargin.left}px`,
            paddingRight: `${bgImgMargin.right}px`,
            paddingBottom: `${bgImgMargin.bottom}px`,
            ...(backgroundMode === 'COLOR' && backgroundColor
              ? { backgroundColor }
              : backgroundMode === 'IMAGE' && backgroundImgUrl
                ? {
                    backgroundImage: `url('${process.env.REACT_APP_IMAGE_PROXY_URL}?url=${backgroundImgUrl}')`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                  }
                : {
                    backgroundImage: `url('/images/BG2025_dark_landscape.png')`,
                    backgroundSize: 'cover',
                  }),
          }}
        >
          <section className="col-span-2 flex flex-col gap-4 p-4 uppercase">
            <div className="mt-6 flex items-center justify-center gap-4">
              <IGImg
                src={metadata.HomeTeamLogo}
                alt=""
                className="aspect-square w-64"
              />
              <LogoOrTitle
                logo={metadata.PhaseLogo}
                title="VS"
                className="w-16"
                logoClassName="object-cover scale-[2.5]"
                titleClassName="text-5xl text-center"
              />
              <IGImg
                src={metadata.AwayTeamLogo}
                alt=""
                className="aspect-square w-64"
              />
            </div>
            <p className="text-5xl">{metadata.HomeTeamName}</p>
            <p className="text-right text-5xl">{metadata.AwayTeamName}</p>
            <div className="mt-6 flex w-auto justify-between text-2xl">
              <p>
                {`${formatDateToLongFormat(metadata.Date)}, ${formatTimeStringToHHMM(metadata.Time)}`}
              </p>
              <p>{metadata.VenueName}</p>
            </div>
          </section>
          <section className="flex flex-col items-center justify-between gap-4 bg-black p-2 text-white">
            <CompetitionLogo
              src={metadata.CompetitionLogo}
              title={metadata.CompetitionName}
              season={metadata.SeasonName}
            />
            <div className="flex flex-col items-center font-header">
              <h2 className="text-8xl">Live</h2>
              <h2 className="text-8xl">Match</h2>
              <h2 className="text-5xl">Broadcast</h2>
            </div>
            <div className="flex flex-col items-center font-header gap-3">
              <h2 className="text-4xl uppercase">from</h2>
              <LogoOrTitle
                logo={metadata.VenueLogo}
                title={metadata.VenueName}
                logoClassName="h-[100px]"
                titleClassName="text-5xl"
              />
            </div>
            </section>
        </article>
      }
    </>
  );
};

export default IG4_YoutubeThumbnail;
